import { useState, useMemo, useEffect, useRef } from 'react'
import SelectableAppointmentCard from 'components/cards/AppointmentCard/SelectableAppointmentCard/SelectableAppointmentCard'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import styles from './ScheduledAppointments.module.sass'

const InfoIcon = () => (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='16'
		height='16'
		viewBox='0 0 24 24'
		fill='currentColor'
	>
		<path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z' />
	</svg>
)

const allowedStatusIds = [1, 2, 3, 5, 9]

const ScheduledAppointments = ({
	appointments = [],
	onSelectionChange,
	onHasFiltered,
	onNoTransferChange,
	appointmentId,
	linkedAppointmentIds = [],
}) => {
	const required = appointmentId == null
	const [included, setIncluded] = useState({})
	const [noTransfer, setNoTransfer] = useState(false)
	const autoSelectedRef = useRef(false)
	const { i18n } = useGlobal()

	const linkedAppointmentsWarning = linkedAppointmentIds.length > 0 && (
		<div className={styles['linked-warning']}>
			<p className={styles['linked-warning-text']}>
				{i18n`These appointments are already included in the transfer:`}{' '}
				<strong>{linkedAppointmentIds.join(', ')}</strong>.
			</p>
		</div>
	)
	const filtered = useMemo(() => {
		return appointments.filter((app) => {
			const isAllowed = allowedStatusIds.includes(app.status?.id)
			return isAllowed
		})
	}, [appointments])

	useEffect(() => {
		onHasFiltered?.(filtered.length > 0)
	}, [filtered])

	useEffect(() => {
		// Only auto-select once: re-running this whenever `filtered`
		// changes reference (e.g. a background refetch) would silently
		// re-include the appointment even after the user explicitly
		// deselected it (e.g. via the "no transfer" checkbox).
		if (autoSelectedRef.current || noTransfer) return
		if (required && filtered.length === 1) {
			autoSelectedRef.current = true
			const id = filtered[0].id
			setIncluded((prev) => {
				if (id in prev) return prev
				const updated = { [id]: true }
				onSelectionChange?.(updated)
				return updated
			})
		}
	}, [filtered, required, noTransfer])

	const toggle = (id) => {
		setIncluded((prev) => {
			const next = !prev[id]
			if (prev[id] === next) return prev

			const updated = { ...prev, [id]: next }
			onSelectionChange(updated)
			return updated
		})
	}

	const handleNoTransferChange = (e) => {
		const checked = e.target.checked
		setNoTransfer(checked)
		onNoTransferChange?.(checked)
		if (checked) {
			setIncluded({})
			onSelectionChange?.({})
		}
	}

	if (filtered.length === 0) {
		return (
			<fieldset>
				<h4>{i18n`Nexts Appointments`}</h4>
				{linkedAppointmentsWarning}
				{!required && (
					<p className={styles['hint-optional']}>
						{i18n`This transfer already has the appointment`}{' '}
						<strong>{appointmentId}</strong>.
					</p>
				)}
				<p className={styles['empty']}>
					{i18n`No future appointments to display`}
				</p>
			</fieldset>
		)
	}

	return (
		<fieldset>
			<h4 className={styles['title']}>
				{i18n`Nexts Appointments`}
				<Tooltip
					displayDelay={200}
					content={
						<span className={styles['tooltip-content']}>
							{i18n`Selected appointments will be included in the transfer and cancelled after submission.`}
						</span>
					}
				>
					<div className={styles['info-icon']}>
						<InfoIcon />
					</div>
				</Tooltip>
			</h4>
			{linkedAppointmentsWarning}
			{!required && (
				<p className={styles['hint-optional']}>
					{i18n`This transfer already has the appointment`}{' '}
					<strong>{appointmentId}</strong>.{' '}
					{i18n`Check the option below or select an additional appointment.`}
				</p>
			)}
			<p className={styles['hint']}>
				{i18n`Select the appointments you want to include in the transfer.`}
			</p>
			<div className={styles['list']}>
				{filtered.map((app) => (
					<SelectableAppointmentCard
						key={app.id}
						data={app}
						checked={!noTransfer && !!included[app.id]}
						onChange={() => toggle(app.id)}
						disabled={noTransfer}
					/>
				))}
			</div>
			<label className={styles['no-transfer']}>
				<input
					type='checkbox'
					checked={noTransfer}
					onChange={handleNoTransferChange}
				/>
				{i18n`Do not transfer any specialty`}
			</label>
		</fieldset>
	)
}

export default ScheduledAppointments
